import { ChatBubbleOvalLeftIcon, HeartIcon, ShareIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useState, useRef, useCallback, createRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Nav from '../components/nav';
import "../styles/Blog.css";
import { FaTrash } from "react-icons/fa";
import { useLocation } from 'react-router';
import { IoIosImages } from "react-icons/io";
import { baseUrl } from '../config';
import Advertisement from '../components/Advertisement';

// Persistent user cache — survives re-fetches within the session (5-min TTL)
const USER_CACHE_TTL = 5 * 60 * 1000;
const userCache = new Map(); // uid → { username, profPic, cachedAt }

function getCachedUser(uid) {
  const entry = userCache.get(uid);
  if (entry && Date.now() - entry.cachedAt < USER_CACHE_TTL) return entry;
  return null;
}

// Loading component for better UX
const LoadingSpinner = () => (
  <div className="flex justify-center items-center p-4">
    <div className="blog-modern-loading"></div>
  </div>
);

// Error message component
const ErrorMessage = ({ message }) => (
  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
    <span className="block sm:inline">{message}</span>
  </div>
);

// Authentication prompt component
const AuthPrompt = () => {
  const navigate = useNavigate();
  
  return (
    <div className="flex justify-center items-center py-20">
      <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md text-center border-2 border-teal-200">
        <div className="mb-6">
          <svg className="w-20 h-20 mx-auto text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-3">Sign In Required</h2>
        <p className="text-gray-600 mb-6">Please sign in to view and create blog posts.</p>
        <button
          onClick={() => navigate('/login')}
          className="blog-modern-post-btn w-full"
        >
          Sign In
        </button>
      </div>
    </div>
  );
};

const Post = ({ post, addComment, likePost, isLoading, postRef, activeUser}) => {
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState([]);
  const [isLiking, setIsLiking] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);
  const postLink = useRef(null);
  const [share, setShare] = useState(false);

  const handleLike = async (postId) => {
    try {
      setIsLiking(true);
      await likePost(postId);
    } catch (error) {
    } finally {
      setIsLiking(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) {
      setCommentError('Comment cannot be empty');
      return;
    }
    
    try {
      setIsAddingComment(true);
      setCommentError('');
      
      const token = localStorage.getItem("access_token");
      const userId = await fetch(`${baseUrl}/getUserIDByToken`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const id = await userId.json();
      
      const response = await fetch(`${baseUrl}/getUsernameById`, {
        headers: {
          user_id: id.user_id
        },
      });
      const data = await response.json();
      
      const img = await fetch(`${baseUrl}/profilePictureById`, {
        headers: {
          user_id: id.user_id
        },
      });
      const imgData = await img.json();
      
      await addComment(post.post_id, newComment);
      setNewComment('');
      
      // Update comments locally
      let localComment = { 
        content: newComment, 
        comment_id: Date.now(), 
        user_id: id.user_id, 
        profPic: imgData, 
        username: data.username
      };
      setComments([...comments, localComment]);
    } catch (error) {
      setCommentError('Failed to add comment. Please try again.');
    } finally {
      setIsAddingComment(false);
    }
  };

  useEffect(() => {
    setComments(post.comments || []);
  }, [post.comments]);
  
  async function deletePost(id){
    try {
      const deletePost = await fetch(`${baseUrl}/deletePost`, {
        method: "DELETE",
        headers: {
          post_id: id,
        }
      });
    } catch (error) {
    }
    window.location.reload();
  }

  function openShare(id){
    if (share) {
      document.getElementById('sharePost' + id).style.display = "block";
      postLink.current.value = window.location.origin + "/blog#" + id;
      setShare(false);
    } else {
      document.getElementById('sharePost' + id).style.display = "none";
      setShare(true);
    }
  }

  return (
    <div className="post blog-modern-post-card bg-white border border-gray-200 rounded-lg shadow-md mb-6 overflow-hidden" key={post.post_id} id={post.post_id} ref={postRef.current[post.post_id]}>
      <div className="post-header flex items-center p-4 postHeader">
        {/* ✅ Post Author Profile Picture */}
        <img 
          src={post.userAvatar || `${baseUrl}/static/img/defaultProf.avif`}
          alt={post.userName} 
          className="blog-modern-profile-avatar w-10 h-10 rounded-full mr-3 postImage object-cover"
          loading="lazy"
          onError={(e) => {
            e.target.src = `${baseUrl}/static/img/defaultProf.avif`;
          }}
        />
        <div>
          <h3 className="blog-modern-username font-semibold text-gray-800">{post.userName}</h3>
          <p className="blog-modern-timestamp text-xs text-gray-500">{post.timestamp}</p>
        </div>
        {activeUser === post.user_id && (
          <FaTrash 
            className='deletePost blog-modern-delete-icon' 
            onClick={() => {deletePost(post.post_id)}}
          />
        )}
      </div>

      {/* Post Image */}
      {post.post_file && post.post_file !== "null" && (
        <div className="blog-modern-post-image">
          <img 
            src={`${baseUrl}/img/${post.post_file}`}
            alt="Post content"
            className="w-full h-auto object-cover"
            loading="lazy"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}
      
      <div className="post-content p-4">
        <p className="blog-modern-post-content text-gray-600">{post.content}</p>
      </div>
      
      <div className="blog-modern-actions post-actions flex justify-between items-center px-4 py-2 border-t border-gray-200">
        <button 
          onClick={() => handleLike(post.post_id)} 
          disabled={isLiking}
          className={`blog-modern-action-btn flex items-center ${post.liked ? 'blog-modern-liked text-red-500' : 'text-gray-500'} hover:text-red-500 transition ${isLiking ? 'opacity-50' : ''}`}
        >
          <HeartIcon className="w-6 h-6 mr-1" />
          <span>{post.likes}</span>
        </button>
        
        <button 
          onClick={() => setShowComments(!showComments)} 
          className="blog-modern-action-btn flex items-center text-gray-500 hover:text-blue-500 transition"
        >
          <ChatBubbleOvalLeftIcon className="w-6 h-6 mr-1" />
          <span>{post.commentCount}</span>
        </button>
        
        <button 
          className="blog-modern-action-btn flex items-center text-gray-500 hover:text-green-500 transition" 
          onClick={() => {openShare(post.post_id)}}
        >
          <ShareIcon className="w-6 h-6 mr-1" />
          <span>Share</span>
        </button>
      </div>

      <input type="text" name="postLink" className="postLink" id={'sharePost' + post.post_id} ref={postLink}/>

      {showComments && (
        <div className="post-comments p-4 bg-gray-50">
          {comments.map((comment) => (
            <div className='commentWrapper blog-modern-comment-wrapper' key={comment.comment_id}>
              <div className='commentUser flex items-center mb-2'>
                {/* ✅ FIXED: Comment Profile Picture with Proper URL Handling */}
                <img 
                  src={comment.profPic || `${baseUrl}/static/img/defaultProf.avif`}
                  alt={comment.username || 'User'} 
                  className='commentProfImg blog-modern-comment-avatar w-8 h-8 rounded-full object-cover mr-2' 
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = `${baseUrl}/static/img/defaultProf.avif`;
                  }}
                />
                <p className='commentUsername blog-modern-comment-username font-semibold'>{comment.username}:</p>
              </div>
              <p className="mb-2 text-gray-700 commentContent ml-10">{comment.content}</p>
            </div>
          ))}
          
          <form onSubmit={handleAddComment} className="mt-4 flex flex-col">
            {commentError && (
              <div className="mb-2 text-red-500 text-sm">{commentError}</div>
            )}
            <div className="flex">
              <input
                type="text"
                value={newComment}
                onChange={(e) => {
                  setNewComment(e.target.value);
                  setCommentError('');
                }}
                placeholder="Add a comment..."
                className={`blog-modern-comment-input flex-grow border ${commentError ? 'border-red-500' : 'border-gray-300'} rounded-l-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                disabled={isAddingComment}
              />
              <button 
                type="submit" 
                className={`blog-modern-comment-btn bg-blue-500 text-white px-4 py-2 rounded-r-md hover:bg-blue-600 transition ${isAddingComment ? 'opacity-50' : ''}`}
                disabled={isAddingComment}
              >
                {isAddingComment ? 'Posting...' : 'Post'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const Blog = () => {
  const isIframe = window.self !== window.top;
  const navigate = useNavigate();
  const [newPost, setNewPost] = useState({ content: '' });
  const [postImage, setPostImage] = useState(null);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [userMessage, setUserMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [postSuccess, setPostSuccess] = useState(false);
  const postRef = useRef(posts.map(() => createRef()));
  const location = useLocation();
  const [activeUser, setActiveUser] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  
  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("access_token");
      
      if (!token) {
        setIsAuthenticated(false);
        setAuthChecked(true);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${baseUrl}/getUserIDByToken`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setAuthChecked(true);
      }
    };

    checkAuth();
  }, []);

  // Validate form inputs
  const validateForm = () => {
    const errors = {};
    if (!newPost.content.trim()) {
      errors.content = 'Content is required';
    }
    return errors;
  };

  // Upload to B2
  async function uploadToR2Direct(file, fileType = 'image') {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', fileType);
      formData.append('user_id', activeUser);

      const response = await fetch(`${baseUrl}/upload/direct`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await response.json();
      return {
        filename: data.filename,
        original_filename: data.original_filename,
        url: data.public_url,
        size: data.size,
        status: 'success',
        metadata_id: data.metadata_id
      };

    } catch (error) {
      throw error;
    }
  }

  // Optimized data fetching using Promise.all
  const fetchAllData = useCallback(async () => {
    console.log('fetchAllData started, isAuthenticated:', isAuthenticated);
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const safeJson = r => r.text().then(t => { try { return JSON.parse(t) } catch(e) { return null } });
      const token = localStorage.getItem("access_token");

      // Wave 1: fetch user identity + all posts in parallel
      const [userIdRes, postsResponse] = await Promise.all([
        fetch(`${baseUrl}/getUserIDByToken`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${baseUrl}/allPosts`),
      ]);
      const id = await userIdRes.json();
      const postsData = await postsResponse.json();
      console.log('user_id:', id.user_id);
      console.log('posts fetched:', postsData.length);
      setActiveUser(id.user_id);

      const reversedPosts = postsData.slice().reverse();

      // Wave 2: fetch comments, likes, isLiked for every post — all in one parallel wave
      const postDataResults = await Promise.all(
        reversedPosts.map(post =>
          Promise.all([
            fetch(`${baseUrl}/getComments`, { headers: { post_id: post.post_id } }).then(safeJson),
            fetch(`${baseUrl}/getLikes`,    { headers: { post_id: post.post_id } }).then(safeJson),
            fetch(`${baseUrl}/isLiked`,     { headers: { post_id: post.post_id, user_id: id.user_id } }).then(safeJson),
          ]).then(([comments, likes, liked]) => ({ post, comments: comments || [], likes, liked }))
        )
      );

      // Collect every unique user_id across posts AND comments
      const uniqueUserIds = new Set();
      for (const { post, comments } of postDataResults) {
        uniqueUserIds.add(post.user_id);
        for (const c of comments) uniqueUserIds.add(c.user_id);
      }

      // Wave 3: fetch username + avatar only for users not already in the session cache
      const uncachedIds = [...uniqueUserIds].filter(uid => !getCachedUser(uid));
      await Promise.all(uncachedIds.map(async uid => {
        const [usernameData, profPicData] = await Promise.all([
          fetch(`${baseUrl}/getUsernameById`,    { headers: { user_id: uid } }).then(safeJson),
          fetch(`${baseUrl}/profilePictureById`, { headers: { user_id: uid } }).then(safeJson),
        ]);
        userCache.set(uid, { username: usernameData?.username || '', profPic: profPicData, cachedAt: Date.now() });
      }));

      // Assemble posts synchronously from cached data — no more fetches
      const enhancedPosts = postDataResults.map(({ post, comments, likes, liked }) => {
        const author = getCachedUser(post.user_id) || {};
        const enhancedComments = comments.map(c => ({
          ...c,
          username: (getCachedUser(c.user_id) || {}).username || c.username,
          profPic:  (getCachedUser(c.user_id) || {}).profPic,
        }));

        console.log('likes raw data:', likes);
        const likesCount = Array.isArray(likes) ? likes.length
          : typeof likes === 'number' ? likes
          : likes?.count ?? 0;

        return {
          ...post,
          userName:     author.username,
          userAvatar:   author.profPic,
          comments:     enhancedComments,
          commentCount: enhancedComments.length,
          likes:        likesCount,
          liked:        liked ?? false,
        };
      });

      console.log('enhanced posts ready:', enhancedPosts.length);
      setPosts(enhancedPosts);
    } catch (err) {
      console.log('fetchAllData ERROR:', err.name, err.message);
      setError('Failed to load posts. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (authChecked) {
      fetchAllData();
      try {
        if (location.hash) {
          const id = location.hash;
          const loc = id.slice(1);
          setTimeout(() => document.getElementById(loc)?.scrollIntoView({ behavior: 'smooth' }), 2000);
        }
      } catch (error) {
      }
    }
  }, [authChecked, fetchAllData, location.hash]);

  useEffect(() => {
    setTimeout(function() {
      setPostSuccess(false);
    }, 5000);
  }, [postSuccess]);

  // Save only UUID filename to database
  const addPost = async (uploadedFilename) => {
    setPostSuccess(false);
    try {
      const token = localStorage.getItem("access_token");
      const userId = await fetch(`${baseUrl}/getUserIDByToken`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const id = await userId.json();
      
      const requestOptions = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: id.user_id,
          content: newPost.content,
          post_file: uploadedFilename || "null",
          post_type: "blog"
        }),
      };
      const response = await fetch(`${baseUrl}/post`, requestOptions);
      if (!response.ok) {
        throw new Error('Failed to create post');
      }
      setPostSuccess(true);
      return await response.json();
    } catch (error) {
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setIsSubmitting(true);
      setFormErrors({});
      
      let uploadedFilename = null;

      // Upload image if selected
      if (postImage) {
        const uploadResult = await uploadToR2Direct(postImage, 'image');
        uploadedFilename = uploadResult.filename;
      }
      
      // Create post with UUID filename
      await addPost(uploadedFilename);
      
      // Reset form
      setNewPost({ content: '' });
      setPostImage(null);
      
      // Refresh posts
      await fetchAllData();
    } catch (error) {
      setFormErrors({ submit: 'Failed to create post. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addComment = async (postId, comment) => {
    const token = localStorage.getItem("access_token");
    const userId = await fetch(`${baseUrl}/getUserIDByToken`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const id = await userId.json();
    
    const response = await fetch(`${baseUrl}/getUsernameById`, {
      headers: {
        user_id: id.user_id
      },
    });
    const data = await response.json();
    
    const img = await fetch(`${baseUrl}/profilePictureById`, {
      headers: {
        user_id: id.user_id
      },
    });
    const imgData = await img.json();
    
    try {
      const response = await fetch(`${baseUrl}/addComment`, {
        method: "POST",
        headers: {
          user_id: id.user_id,
          post_id: postId,
          content: comment,
          profPic: imgData,
          username: data.username
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to add comment');
      }

      // Update posts state with new comment
      const updatedPosts = posts.map(post => {
        if (post.post_id === postId) {
          return {
            ...post,
            comments: [...post.comments, { 
              content: comment, 
              comment_id: Date.now(), 
              profPic: imgData, // ✅ Full URL from backend
              username: data.username,
              user_id: id.user_id
            }],
            commentCount: post.commentCount + 1
          };
        }
        return post;
      });
      setPosts(updatedPosts);
      return await response.json();
    } catch (error) {
      throw error;
    }
  };

  const likePost = async (postId) => {
    try {
      const token = localStorage.getItem("access_token");
      const userId = await fetch(`${baseUrl}/getUserIDByToken`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const id = await userId.json();
      
      const response = await fetch(`${baseUrl}/like`, {
        method: "POST",
        headers: {
          post_id: postId,
          user_id: id.user_id
        }
      });

      if (!response.ok) {
        throw new Error('Failed to like post');
      }

      // Update posts state with new like status
      const updatedPosts = posts.map(post => {
        if (post.post_id === postId) {
          return {
            ...post,
            liked: !post.liked,
            likes: post.liked ? post.likes - 1 : post.likes + 1
          };
        }
        return post;
      });
      setPosts(updatedPosts);
    } catch (error) {
      throw error;
    }
  };

  if (isIframe) return null;

  return (
    <>
      <Nav />
      <div className="blog-modern-page-wrapper bg-gray-100 min-h-screen">
        <Advertisement />
        <div className="mx-auto pt-8 px-4 containers">
          {/* Create post form - only show if authenticated */}
          {isAuthenticated && (
            <div className="blog-modern-create-form bg-white border border-gray-300 rounded-lg shadow-lg mb-8 p-6">
              <h2 className="text-2xl font-bold mb-4 text-gray-800">Create a Post</h2>
              <form onSubmit={handleSubmit} className="space-y-4">            
                <div>
                  <textarea
                    value={newPost.content}
                    onChange={(e) => {
                      setNewPost({ ...newPost, content: e.target.value });
                      setFormErrors({ ...formErrors, content: '' });
                    }}
                    placeholder="What's on your mind?"
                    className={`blog-modern-textarea h-40 mb-4 border-2 ${formErrors.content ? 'border-red-500' : 'border-gray-300'} p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  {formErrors.content && (
                    <p className="text-red-500 text-sm mt-1">{formErrors.content}</p>
                  )}
                </div>

                <div className="flex items-center justify-between flex-wrap gap-4">
                  {/* Action Group: Image + AI Assistant */}
                  <div className="blog-modern-action-group">
                    <label htmlFor="chatLogo" id='imageSelectButton'>
                      <span><IoIosImages id='imageUpload'/> Select Image</span>
                    </label>
                    
                    <button 
                      type="button"
                      onClick={() => setChatModalOpen(true)} 
                      className="blog-modern-ai-btn"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                      </svg>
                      AI Assistant
                    </button>
                  </div>

                  <div>
                    <input 
                      type="file" 
                      name="chatLogo" 
                      id="chatLogo" 
                      onChange={(e) => {
                        if(e.target.files[0].size > 10485760) {
                          alert("File is too big!");
                          e.target.value = "";
                        }
                        else{
                          setPostImage(e.target.files[0])
                        }
                      }}
                      accept='image/png, image/jpeg, image/gif, image/webp'
                    />
                    {postImage !== null && <p className="text-sm text-teal-600 mt-2">📎 {postImage.name}</p>}
                  </div>

                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className={`blog-modern-post-btn ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="blog-modern-loading mr-2"></span>
                        Posting...
                      </>
                    ) : (
                      'Post'
                    )}
                  </button>
                </div>
                
                {formErrors.submit && (
                  <p className="text-red-500 text-sm mt-2">{formErrors.submit}</p>
                )}
              </form>
            </div>
          )}
          
          {/* Post success message */}
          {postSuccess && (
            <div className="blog-modern-success mb-6">
              ✅ Successfully Posted!
            </div>
          )}

          {/* Error state */}
          {error && <ErrorMessage message={error} />}

          {/* Content area - show auth prompt if not authenticated */}
          {!authChecked ? (
            <LoadingSpinner />
          ) : !isAuthenticated ? (
            <AuthPrompt />
          ) : isLoading ? (
            <LoadingSpinner />
          ) : (
            /* Posts */
            posts.map((post) => (
              <Post 
                key={post.post_id}
                id={post.post_id}
                post={post} 
                addComment={addComment} 
                likePost={likePost}
                isLoading={isLoading}
                postRef={postRef}
                activeUser={activeUser}
              />
            ))
          )}
        </div>

        {/* AI Assistant Modal */}
        {chatModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-2xl p-6 w-96 max-w-full">
              <h3 className="text-2xl font-bold mb-4 text-gray-800">✨ AI Writing Assistant</h3>
              <div className="h-64 overflow-y-auto mb-4 bg-gray-100 p-4 rounded-md shadow-inner">
                {chatMessages.length === 0 ? (
                  <div className="text-center text-gray-500 mt-20">
                    <p>💡 Ask me to help you write better content!</p>
                    <p className="text-sm mt-2">Try: "Give me post ideas" or "Improve my writing"</p>
                  </div>
                ) : (
                  chatMessages.map((message, index) => (
                    <div 
                      key={index} 
                      className={`mb-2 p-2 rounded-lg w-3/6 ${
                        message.sender === 'ai' 
                          ? 'bg-blue-100 text-blue-800 aiMessage' 
                          : 'bg-green-100 text-green-800 ml-4 userMessage'
                      }`}
                    >
                      {message.text}
                    </div>
                  ))
                )}
              </div>
              
              <input
                type="text"
                value={userMessage}
                onChange={(e) => setUserMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && userMessage.trim()) {
                    const newMessage = { sender: 'user', text: userMessage };
                    setChatMessages([...chatMessages, newMessage]);
                    setUserMessage('');
                    setTimeout(() => {
                      setChatMessages(prev => [...prev, { 
                        sender: 'ai', 
                        text: 'Great question! Here are some suggestions to improve your content...' 
                      }]);
                    }, 1000);
                  }
                }}
                placeholder="Type your message..."
                className="border-2 border-gray-300 p-2 w-full rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              
              <div className="flex justify-between gap-3">
                <button
                  onClick={() => {
                    if (userMessage.trim()) {
                      const newMessage = { sender: 'user', text: userMessage };
                      setChatMessages([...chatMessages, newMessage]);
                      setUserMessage('');
                      setTimeout(() => {
                        setChatMessages(prev => [...prev, { 
                          sender: 'ai', 
                          text: 'Great question! Here are some suggestions to improve your content...' 
                        }]);
                      }, 1000);
                    }
                  }}
                  className="blog-modern-ai-btn flex-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                  </svg>
                  Send
                </button>
                
                <button
                  onClick={() => {
                    setChatModalOpen(false);
                    setChatMessages([]);
                  }}
                  className="bg-red-500 text-white py-2 px-6 rounded-full shadow-md hover:bg-red-600 transition flex items-center closeButton"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Blog;
